import { useEffect, useMemo, useState } from 'react';
import useMqttSubscribe from '@hooks/useMqttSubscribe';
import { useMqttStore } from '@stores/mqttStore';
import type { CalibrationRunnerLiveContext, MeasurementSide } from '../types';

type RunnerMeasurementMessage = {
  testPoint?: string | null;
  testPointId?: string | null;
  targetField?: 'asFound' | 'asLeft';
  nominal?: number | null;
  measured?: number | null;
  measuredText?: string | null;
  unit?: string | null;
  imageId?: string | null;
};

type RunnerItemStatusMessage = {
  runId?: string;
  itemGlobalId?: string;
  status?: string;
  testPointId?: string | null;
};

type RunnerRunStatusMessage = {
  runId?: string;
  status?: string;
  currentItemIndex?: number;
  totalItems?: number;
  errorMessage?: string | null;
};

export type LiveSideReading = {
  value: number;
  unit?: string | null;
  imageId?: string | null;
};

export type LiveReadingsByPoint = Record<
  string,
  Partial<Record<MeasurementSide, LiveSideReading>>
>;

export type RunnerLiveFillResult = {
  liveReadings: LiveReadingsByPoint;
  currentPointId: string | null;
  landedPointId: string | null;
  landedSide: MeasurementSide | null;
  landedAt: number | null;
  runStatus: string | null;
  currentItemIndex: number;
  totalItems: number;
};

const EMPTY_RESULT: RunnerLiveFillResult = {
  liveReadings: {},
  currentPointId: null,
  landedPointId: null,
  landedSide: null,
  landedAt: null,
  runStatus: null,
  currentItemIndex: 0,
  totalItems: 0
};

export const useRunnerLiveFill = (
  live: CalibrationRunnerLiveContext | undefined
): RunnerLiveFillResult => {
  const browserStationId = useMqttStore(s => s.stationId);
  const effectiveStationId = live?.station ?? browserStationId;
  const enabled = !!live?.runId && !!effectiveStationId;

  const topicPrefix = enabled
    ? `voltbot/${effectiveStationId}/script/run/${live?.runId}`
    : '';

  const { lastMessage: measurementMessage } =
    useMqttSubscribe<RunnerMeasurementMessage>(
      enabled ? `${topicPrefix}/measurement` : '',
      { enabled }
    );

  const { lastMessage: itemStatusMessage } =
    useMqttSubscribe<RunnerItemStatusMessage>(
      enabled ? `${topicPrefix}/item/+/status` : '',
      { enabled }
    );

  const { lastMessage: runStatusMessage } =
    useMqttSubscribe<RunnerRunStatusMessage>(
      enabled ? `${topicPrefix}/status` : '',
      { enabled }
    );

  const [liveReadings, setLiveReadings] = useState<LiveReadingsByPoint>({});
  const [landed, setLanded] = useState<{
    pointId: string;
    side: MeasurementSide;
    at: number;
  } | null>(null);
  const [currentPointId, setCurrentPointId] = useState<string | null>(null);

  useEffect(() => {
    setLiveReadings({});
    setLanded(null);
    setCurrentPointId(null);
  }, [live?.runId]);

  useEffect(() => {
    if (!measurementMessage) return;

    const pointId = measurementMessage.testPointId;
    const value = measurementMessage.measured;
    if (!pointId || value === null || value === undefined) return;

    const side: MeasurementSide =
      measurementMessage.targetField === 'asLeft' ? 'asLeft' : 'asFound';

    setLiveReadings(prev => ({
      ...prev,
      [pointId]: {
        ...prev[pointId],
        [side]: {
          value,
          unit: measurementMessage.unit,
          imageId: measurementMessage.imageId
        }
      }
    }));
    setLanded({ pointId, side, at: Date.now() });
  }, [measurementMessage]);

  useEffect(() => {
    if (!itemStatusMessage) return;

    if (itemStatusMessage.status === 'RUNNING') {
      setCurrentPointId(itemStatusMessage.testPointId ?? null);
    } else {
      setCurrentPointId(null);
    }
  }, [itemStatusMessage]);

  return useMemo(() => {
    if (!enabled) return EMPTY_RESULT;

    return {
      liveReadings,
      currentPointId,
      landedPointId: landed?.pointId ?? null,
      landedSide: landed?.side ?? null,
      landedAt: landed?.at ?? null,
      runStatus: runStatusMessage?.status ?? null,
      currentItemIndex: runStatusMessage?.currentItemIndex ?? 0,
      totalItems: runStatusMessage?.totalItems ?? 0
    };
  }, [enabled, liveReadings, currentPointId, landed, runStatusMessage]);
};
